// ===== Editar Ficha do Cliente (dados gerais, contatos múltiplos, fiscal, endereços) =====
import React from 'react';
import { Icon } from './icons.jsx';
import { useStore } from './store.jsx';
import { usePermission } from './auth.jsx';
import { FormField, Input, ModalShell, Select, OPCOES_CONTRIBUINTE } from './modals.jsx';
import {
  DIAS_INATIVIDADE, ERP_CONFIG, NICHOS, TIPO_CLIENTE,
  buildErpClientePayload, maskCEP, maskCNPJ, maskCPF, validateCNPJ, validateCPF,
} from './data.jsx';

const { useState: useStateCE } = React;

const EMPTY_ADDR = { cep: "", rua: "", num: "", complemento: "", bairro: "", cidade: "", uf: "" };

function AddrFields({ value, onChange, prefix }) {
  const v = { ...EMPTY_ADDR, ...(value || {}) };
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="addr-grid">
      <FormField label="CEP" style={{ gridColumn: "span 3" }}><Input className="mono" placeholder="00000-000" value={v.cep} onChange={e => set("cep", maskCEP(e.target.value))} /></FormField>
      <FormField label="Logradouro" style={{ gridColumn: "span 7" }}><Input placeholder="Rua / Avenida" value={v.rua} onChange={e => set("rua", e.target.value)} /></FormField>
      <FormField label="Número" style={{ gridColumn: "span 2" }}><Input placeholder="nº" value={v.num} onChange={e => set("num", e.target.value)} /></FormField>
      <FormField label="Complemento" style={{ gridColumn: "span 3" }}><Input placeholder="Sala / Apto" value={v.complemento} onChange={e => set("complemento", e.target.value)} /></FormField>
      <FormField label="Bairro" style={{ gridColumn: "span 3" }}><Input value={v.bairro} onChange={e => set("bairro", e.target.value)} /></FormField>
      <FormField label="Cidade" style={{ gridColumn: "span 4" }}><Input value={v.cidade} onChange={e => set("cidade", e.target.value)} /></FormField>
      <FormField label="UF" style={{ gridColumn: "span 2" }}><Input placeholder="UF" maxLength={2} value={v.uf} onChange={e => set("uf", e.target.value.toUpperCase())} /></FormField>
    </div>
  );
}

function EditClienteModal({ clienteId, onClose }) {
  const store = useStore();
  const cliente = store.getClienteById(clienteId);
  if (!cliente) return null;

  const initContatos = (cliente.contatos && cliente.contatos.length > 0)
    ? cliente.contatos.map(c => ({ ...c }))
    : [{ id: "ct_p", nome: cliente.contato || "", cargo: cliente.cargo || "", telefone: cliente.telefone || "", email: cliente.email || "", principal: true }];

  // O ERP e de quem tem `clientes.sync_erp`; o botao aparecia para todo mundo
  // que abria a ficha, e a Edge Function nao conferia.
  const podeSyncErp = usePermission('clientes.sync_erp');

  const [form, setForm] = useStateCE({
    nome: cliente.nome,
    // Antes havia um campo so, "Razao social / nome", que gravava so em `nome`. A
    // razao social e o tipo de contribuinte vao ao ERP e decidem o ICMS-ST
    // (decisao do Kaio, 30/09).
    razaoSocial: cliente.razaoSocial || "",
    tipoContribuinte: cliente.tipoContribuinte == null ? "" : String(cliente.tipoContribuinte),
    tipo: cliente.tipo,
    nicho: cliente.nicho || "",
    canalId: cliente.canalId || "",
    tabelaId: cliente.tabelaId || "",
    cidade: cliente.cidade || "",
    contatos: initContatos,
    tipoDoc: cliente.tipoDoc || "cnpj",
    cnpj: cliente.cnpj === "—" ? "" : (cliente.cnpj || ""),
    cpf: cliente.cpf || "",
    ie: cliente.ie || "",
    emailFinanceiro: cliente.emailFinanceiro || "",
    condPagamento: cliente.condPagamento || "28 dias",
    endereco: { ...EMPTY_ADDR, ...(cliente.endereco || {}) },
    enderecoEntregaMesmo: cliente.enderecoEntregaMesmo !== false,
    enderecoEntrega: { ...EMPTY_ADDR, ...(cliente.enderecoEntrega || {}) },
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ----- contatos -----
  const setContato = (id, patch) => set("contatos", form.contatos.map(c => c.id === id ? { ...c, ...patch } : c));
  const addContato = () => set("contatos", [...form.contatos, { id: "ct_" + Date.now().toString(36), nome: "", cargo: "", telefone: "", email: "", principal: form.contatos.length === 0 }]);
  const removeContato = (id) => {
    let next = form.contatos.filter(c => c.id !== id);
    if (next.length > 0 && !next.some(c => c.principal)) next = next.map((c, i) => ({ ...c, principal: i === 0 }));
    set("contatos", next);
  };
  const setPrincipal = (id) => set("contatos", form.contatos.map(c => ({ ...c, principal: c.id === id })));

  const docOk = form.tipoDoc === "cnpj" ? validateCNPJ(form.cnpj) : validateCPF(form.cpf);

  const copiarFaturamentoParaEntrega = () => set("enderecoEntrega", { ...form.endereco });
  const copiarEntregaParaFaturamento = () => set("endereco", { ...form.enderecoEntrega });
  const [erpPreview, setErpPreview] = useStateCE(false);

  const submit = () => {
    if (!form.nome.trim()) { store.toast({ kind: "err", msg: "Nome fantasia é obrigatório" }); return; }
    const contatos = form.contatos.filter(c => c.nome.trim());
    if (contatos.length === 0) { store.toast({ kind: "err", msg: "Cadastre ao menos um contato" }); return; }
    const principal = contatos.find(c => c.principal) || contatos[0];
    store.updateCliente(clienteId, {
      nome: form.nome.trim(),
      razaoSocial: form.razaoSocial.trim(),
      tipoContribuinte: form.tipoContribuinte === "" ? null : Number(form.tipoContribuinte),
      tipo: form.tipo,
      // So vai quando MUDOU. Se os nichos nao carregaram, a tela abre sem nicho e um
      // `null` aqui apagaria no banco o nicho que o cliente tem; e todo salvar
      // dependeria do mapa de nichos, mesmo editando so o nome.
      ...(form.nicho !== (cliente.nicho || "") ? { nicho: form.nicho || null } : {}),
      canalId: form.canalId || null,
      tabelaId: form.tabelaId || null,
      cidade: form.cidade,
      contatos,
      contato: principal.nome,
      cargo: principal.cargo,
      telefone: principal.telefone,
      email: principal.email,
      tipoDoc: form.tipoDoc,
      cnpj: form.tipoDoc === "cnpj" ? form.cnpj : "",
      cpf: form.tipoDoc === "cpf" ? form.cpf : "",
      ie: form.tipoDoc === "cnpj" ? (form.ie || "isento") : "isento",
      emailFinanceiro: form.emailFinanceiro,
      condPagamento: form.condPagamento,
      endereco: form.endereco,
      enderecoEntregaMesmo: form.enderecoEntregaMesmo,
      enderecoEntrega: form.enderecoEntregaMesmo ? null : form.enderecoEntrega,
    });
    store.toast({ kind: "ok", msg: `Ficha de "${form.nome}" atualizada` });
    onClose();
  };

  return (
    <ModalShell title={`Editar ficha · ${cliente.nome}`} icon="pencil" onClose={onClose} width={760} footer={
      <>
        {!docOk && <span className="doc-valid err" style={{ marginRight: "auto" }}><Icon name="alert-triangle" size={10} /> fiscal incompleto · bloqueia propostas</span>}
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={submit}><Icon name="save" size={13} /> Salvar ficha</button>
      </>
    }>
      {/* Dados gerais */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
        <FormField label="Nome fantasia" required>
          <Input icon="tag" value={form.nome} onChange={e => set("nome", e.target.value)} autoFocus />
        </FormField>
        <FormField label="Cidade / UF">
          <Input icon="globe" placeholder="São Paulo · SP" value={form.cidade} onChange={e => set("cidade", e.target.value)} />
        </FormField>
      </div>
      <FormField label="Razão social" hint="como consta no CNPJ; vai ao ERP e à nota">
        <Input icon="tag" placeholder="Em branco, o ERP usa o nome fantasia" value={form.razaoSocial} onChange={e => set("razaoSocial", e.target.value)} />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <FormField label="Tipo de cliente">
          <Select value={form.tipo} onChange={v => set("tipo", v)} options={TIPO_CLIENTE.map(t => ({ value: t.id, label: t.label }))} />
        </FormField>
        <FormField label="Nicho de atuação">
          <Select value={form.nicho} onChange={v => set("nicho", v)} options={[{ value: "", label: "Sem nicho" }, ...NICHOS.map(n => ({ value: n.id, label: n.label }))]} />
        </FormField>
        <FormField label="Tabela de preços">
          <Select value={form.tabelaId} onChange={v => set("tabelaId", v)} options={(store.tabelasPreco || []).map(t => ({ value: t.id, label: t.nome }))} />
        </FormField>
      </div>
      {form.tipo === "projeto_canal" && (
        <FormField label="Canal / Especificador">
          <Select value={form.canalId} onChange={v => set("canalId", v)} options={[{ value: "", label: "Sem canal" }, ...store.especificadores.map(e => ({ value: e.id, label: `${e.nome} · ${e.tipo}` }))]} />
        </FormField>
      )}
      <div className="reco-note" style={{ marginTop: 2 }}>
        <Icon name="info" size={11} />
        Status da carteira é calculado automaticamente: <strong>inativo após {DIAS_INATIVIDADE} dias sem compras</strong>; quem nunca comprou fica em Prospecção.
      </div>

      {/* Contatos */}
      <div className="section-h" style={{ marginTop: 16 }}>Contatos <span>· o principal aparece nas listagens</span></div>
      <div className="edit-contato-list">
        {form.contatos.map((ct) => (
          <div key={ct.id} className={`edit-contato ${ct.principal ? "main" : ""}`}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <FormField label="Nome"><Input icon="user" placeholder="Nome do contato" value={ct.nome} onChange={e => setContato(ct.id, { nome: e.target.value })} /></FormField>
              <FormField label="Cargo / papel"><Input placeholder="Ex.: Comprador, Financeiro" value={ct.cargo} onChange={e => setContato(ct.id, { cargo: e.target.value })} /></FormField>
              <FormField label="Telefone"><Input icon="phone" placeholder="+55 11 99999-9999" value={ct.telefone} onChange={e => setContato(ct.id, { telefone: e.target.value })} /></FormField>
              <FormField label="E-mail"><Input icon="mail" placeholder="contato@empresa.com.br" value={ct.email} onChange={e => setContato(ct.id, { email: e.target.value })} /></FormField>
            </div>
            <div className="edit-contato-foot">
              <label className="check-row" style={{ fontSize: 12 }}>
                <input type="radio" name="ct-principal" checked={!!ct.principal} onChange={() => setPrincipal(ct.id)} />
                <span>Contato principal</span>
              </label>
              {form.contatos.length > 1 && (
                <button type="button" className="btn ghost sm" style={{ color: "var(--color-danger)" }} onClick={() => removeContato(ct.id)}>
                  <Icon name="x" size={12} /> Remover
                </button>
              )}
            </div>
          </div>
        ))}
        <button type="button" className="contato-add" onClick={addContato}><Icon name="plus" size={12} /> Adicionar contato</button>
      </div>

      {/* Fiscal */}
      <div className="section-h" style={{ marginTop: 16 }}>
        Cadastro fiscal
        {docOk
          ? <span className="doc-valid ok"><Icon name="check" size={10} /> válido</span>
          : <span className="doc-valid err"><Icon name="alert-triangle" size={10} /> obrigatório para gerar propostas</span>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <FormField label="Tipo">
          <Select value={form.tipoDoc} onChange={v => set("tipoDoc", v)} options={[{ value: "cnpj", label: "Pessoa Jurídica" }, { value: "cpf", label: "Pessoa Física" }]} />
        </FormField>
        {form.tipoDoc === "cnpj" ? (
          <FormField label="CNPJ" required>
            <Input placeholder="00.000.000/0000-00" value={form.cnpj} onChange={e => set("cnpj", maskCNPJ(e.target.value))} />
          </FormField>
        ) : (
          <FormField label="CPF" required>
            <Input placeholder="000.000.000-00" value={form.cpf} onChange={e => set("cpf", maskCPF(e.target.value))} />
          </FormField>
        )}
        {form.tipoDoc === "cnpj" && (
          <FormField label="Inscrição Estadual">
            <Input placeholder="isento" value={form.ie} onChange={e => set("ie", e.target.value)} />
          </FormField>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
        <FormField label="Contribuinte de ICMS" hint="define o ICMS-ST; em branco o ERP infere pela IE, e a inferência nunca produz 'isento'">
          <Select value={form.tipoContribuinte} onChange={v => set("tipoContribuinte", v)} options={OPCOES_CONTRIBUINTE} />
        </FormField>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="E-mail de faturamento · NF-e">
          <Input icon="mail" placeholder="financeiro@empresa.com.br" value={form.emailFinanceiro} onChange={e => set("emailFinanceiro", e.target.value)} />
        </FormField>
        <FormField label="Condição de pagamento padrão">
          <Select value={form.condPagamento} onChange={v => set("condPagamento", v)} options={["À vista", "28 dias", "30/60", "30/60/90"]} />
        </FormField>
      </div>

      {/* Endereços */}
      <div className="section-h" style={{ marginTop: 16 }}>Endereço de faturamento / cobrança</div>
      <AddrFields value={form.endereco} onChange={v => set("endereco", v)} />
      {!form.enderecoEntregaMesmo && (
        <button type="button" className="btn ghost sm" style={{ marginTop: 2 }} onClick={copiarEntregaParaFaturamento}>
          <Icon name="copy" size={12} /> Repetir endereço de entrega na cobrança
        </button>
      )}

      <div className="section-h" style={{ marginTop: 14 }}>Endereço de entrega</div>
      <label className="check-row" style={{ marginBottom: 8 }}>
        <input type="checkbox" checked={form.enderecoEntregaMesmo} onChange={e => set("enderecoEntregaMesmo", e.target.checked)} />
        <span>Entregar no mesmo endereço de faturamento</span>
      </label>
      {!form.enderecoEntregaMesmo && (
        <>
          <AddrFields value={form.enderecoEntrega} onChange={v => set("enderecoEntrega", v)} />
          <button type="button" className="btn ghost sm" style={{ marginTop: 2 }} onClick={copiarFaturamentoParaEntrega}>
            <Icon name="copy" size={12} /> Copiar do faturamento
          </button>
        </>
      )}

      {/* Integração ERP — Nomus */}
      <div className="section-h" style={{ marginTop: 16 }}>
        Integração ERP · Nomus EC
        {cliente.erpId
          ? <span className="doc-valid ok"><Icon name="check" size={10} /> sincronizado · ID {cliente.erpId}</span>
          : <span className="doc-valid err"><Icon name="alert-triangle" size={10} /> não sincronizado</span>}
      </div>
      <div className="erp-block">
        <div className="erp-h">
          <span className="erp-ic"><Icon name="server" size={13} /></span>
          <div style={{ flex: 1 }}>
            <div className="erp-title">{ERP_CONFIG.nome}</div>
            <div className="erp-sub mono">{ERP_CONFIG.baseUrl}/clientes{cliente.erpId ? `/${cliente.erpId}` : ""}</div>
          </div>
          <span className={`erp-status ${ERP_CONFIG.conectado ? "on" : ""}`}><span className="d" /> {ERP_CONFIG.conectado ? "Conectado" : "Offline"}</span>
        </div>
        <div className="erp-actions">
          {podeSyncErp && (
            <button type="button" className="btn sm" onClick={() => { store.syncClienteERP(clienteId); }} disabled={!docOk} style={{ opacity: docOk ? 1 : 0.5 }}>
              <Icon name="refresh-cw" size={12} /> {cliente.erpId ? "Sincronizar com Nomus" : "Criar no Nomus ERP"}
            </button>
          )}
          <button type="button" className="btn ghost sm" onClick={() => setErpPreview(p => !p)}>
            <Icon name="terminal" size={12} /> {erpPreview ? "Ocultar payload" : "Ver payload POST /clientes"}
          </button>
          {podeSyncErp && !docOk && <span style={{ fontSize: 11, color: "var(--color-warn)" }}>Cadastro fiscal válido habilita a sincronização.</span>}
        </div>
        {erpPreview && (
          <pre className="erp-payload">{JSON.stringify(buildErpClientePayload({ ...cliente, ...form, cnpj: form.tipoDoc === "cnpj" ? form.cnpj : "", cpf: form.tipoDoc === "cpf" ? form.cpf : "", ie: form.tipoDoc === "cnpj" ? (form.ie || "isento") : "isento", endereco: form.endereco, telefone: cliente.telefone, email: cliente.email }), null, 2)}</pre>
        )}
      </div>
    </ModalShell>
  );
}

export { EditClienteModal, AddrFields };
